/* eslint-disable @typescript-eslint/no-use-before-define */
import { Button } from '@studio/design-system/button';
import type { Api as DialogApi, Props as DialogProps } from '@studio/design-system/dialog';
import { Icon } from '@studio/design-system/icon';
import { Input } from '@studio/design-system/input';
import { Message, Prompt } from '@studio/design-system/prompt';
import T from '@studio/design-system/themes';
import type { Room } from '@studio/models/room';
import { useLatestFn } from '@studio/react-utils/hooks';
import { forwardRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { Opaque } from 'type-fest';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { Paths } from '#~/routes/index.js';

/** @internal */
export type CreateRoomDialogProps = DialogProps & {
  /** Called when the dialog is confirmed. */
  onConfirm: (
    name: string,
    onSuccess: (id: Opaque<string, '__roomId'>, handleClose: () => void) => void,
    onError: () => void
  ) => void;

  /** Called when the dialog is dismissed. */
  onDismiss: () => void;

  /** The organization ID. */
  organizationId: Uint8Array;

  /** Default name to pre-populate the input field. */
  defaultName?: T_Maybe<string>;
};

/** @internal */
export const CreateRoomDialog = forwardRef<DialogApi, CreateRoomDialogProps>(
  (
    { onConfirm, onDismiss: _, organizationId: _orgId, defaultName },
    forwardedRef
  ): ReactElement => {
    const [roomName, setOrgName] = useState<string>(defaultName ?? '');
    const [isBusy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    const handleSuccess = useLatestFn(
      (id: Opaque<string, '__roomId'>, _handleClose: () => void) => {
        setBusy(false);
        handleLaunchStudio(id);
      }
    );

    const handleError = useLatestFn(() => {
      setBusy(false);
      setError('Failed to create studio. Please try again.');
    });

    const handleSave = useLatestFn(() => {
      if (!roomName || roomName.length === 0) {
        setError('Studio name is required.');

        return;
      }

      setError(undefined);
      setBusy(true);

      onConfirm(roomName, handleSuccess, handleError);
    });

    const handleChange = useLatestFn((name: string) => {
      setOrgName(name);
      setError(undefined);
    });

    const handleLaunchStudio = useLatestFn((roomId: Room['id']) => {
      window.location.href = Paths.room.resolve(roomId);

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    });

    const content = (
      <>
        <div className='mb-32 mt-16 w-full overflow-hidden'>
          <Icon className='mb-16 text-[128px] text-primary' icon='home' />
          <div className='mb-32 text-lg font-demibold'>
            {(roomName?.length ?? 0) > 0 ? roomName : 'My Studio'}
          </div>

          <hr />
        </div>

        <h1>Create A Studio</h1>
        <p className='leading-150'>
          The Studio is your central hub for camera management and team collaboration, usually named
          after where your cameras are physically located.
        </p>
        <div className='mt-32'>
          {!!error && <p className='text-error'>{error}</p>}
          <Input
            className='mt-4'
            data-theme={T.getTheme('light')}
            onChange={handleChange}
            placeholder='Studio Name'
            size='xl'
            type='text'
            value={roomName}
          />
        </div>

        <div>
          <Button
            className='mx-auto mt-32 px-64'
            data-theme={T.getTheme('light')}
            disabled={isBusy || !roomName}
            onClick={handleSave}
            size='xl'
            type='button'
          >
            {isBusy ? 'Creating Studio...' : 'Create Studio'}
          </Button>
        </div>
      </>
    );

    return (
      <div>
        <Prompt className='w-[425px]' message={<Message>{content}</Message>} ref={forwardedRef} />
      </div>
    );
  }
);
